import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { loadPrograms } from "../utils/programStorage";
import { loadAssignments } from "../utils/assignmentStorage";
import { loadKasus } from "../utils/auditStorage";
import { loadHariLibur } from "../utils/hariLiburStorage";
import { run60HKEngine } from "../utils/notifEngine60HK";
import {
  runEngine,
  mergeWithStored,
  scopeNotifsForUser,
} from "../utils/notificationEngine";
import { useAuth } from "./AuthContext";
import { api } from "../utils/auth";

const STORAGE_KEY = "irbansus_notifications";
const NotificationContext = createContext(null);

function readStored() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStored(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota errors */
  }
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [tick, setTick] = useState(0); // refresh trigger

  // Run engine on mount, on user change, and on explicit refresh.
  useEffect(() => {
    let mounted = true;

    async function runEngines() {
      const [programs, assignments, kasusList] = await Promise.all([
        loadPrograms(),
        loadAssignments(),
        loadKasus(),
      ]);
      const hariLibur = loadHariLibur();

      if (!mounted) return;

      // Phase 6A: program-level rules
      const fresh = runEngine(programs, assignments);

      // Phase 6B: 60HK engine — generate in-app notifications (read-only, no DB write from here)
      if (kasusList.length > 0) {
        try {
          const r = await api.get("/users");
          if (!mounted) return;
          const userData = Array.isArray(r.data) ? r.data : [];
          const { inAppNotifs } = run60HKEngine({
            kasusList,
            users: userData,
            hariLibur,
          });
          const combined = [...fresh, ...inAppNotifs];
          const stored = readStored();
          const merged = mergeWithStored(combined, stored);
          writeStored(merged);
          if (mounted) setNotifications(merged);
        } catch {
          // Fallback without users list
          const { inAppNotifs } = run60HKEngine({
            kasusList,
            users: [],
            hariLibur,
          });
          const combined = [...fresh, ...inAppNotifs];
          const stored = readStored();
          const merged = mergeWithStored(combined, stored);
          writeStored(merged);
          if (mounted) setNotifications(merged);
        }
        return;
      }

      const stored = readStored();
      const merged = mergeWithStored(fresh, stored);
      writeStored(merged);
      if (mounted) setNotifications(merged);
    }

    runEngines().catch(() => {});
    return () => { mounted = false; };
  }, [user?.id, tick]);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const scoped = useMemo(
    () => scopeNotifsForUser(user, notifications),
    [user, notifications],
  );

  const unreadCount = useMemo(() => scoped.filter((n) => !n.read).length, [scoped]);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, read: true } : n));
      writeStored(next);
      return next;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => {
      // Only mark items visible to the current user as read.
      const visibleIds = new Set(scopeNotifsForUser(user, prev).map((n) => n.id));
      const next = prev.map((n) => (visibleIds.has(n.id) ? { ...n, read: true } : n));
      writeStored(next);
      return next;
    });
  }, [user]);

  const value = useMemo(
    () => ({
      notifications: scoped,
      allNotifications: notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      refresh,
    }),
    [scoped, notifications, unreadCount, markAsRead, markAllAsRead, refresh],
  );

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotifications must be used within NotificationProvider");
  return ctx;
}
